'use client';

import { useLocale } from 'next-intl';
import { Download, File } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useState } from 'react';
import { toast } from 'sonner';
type DocumentType = 'privacy-policy' | 'terms-of-service' | 'refund-policy';
interface DocumentDownloadProps {
  document: DocumentType;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary';
}
const DOCUMENT_NAMES: Record<DocumentType, Record<string, string>> = {
  'privacy-policy': {
    en: 'Privacy Policy',
    zh: '隐私政策'
  },
  'terms-of-service': {
    en: 'Terms of Service',
    zh: '服务条款'
  },
  'refund-policy': {
    en: 'Refund Policy',
    zh: '退款政策'
  }
};
export function DocumentDownload({
  document: documentType,
  className = '',
  variant = 'outline'
}: DocumentDownloadProps) {
  const locale = useLocale();
  const [isLoading, setIsLoading] = useState(false);
  const handleDownload = async (format: 'md' | 'txt') => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/documents/download?doc=${documentType}&locale=${locale}&format=${format}`);
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${documentType}-${locale}.${format === 'txt' ? 'txt' : 'md'}`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success(locale === 'zh' ? '文档下载成功' : 'Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(locale === 'zh' ? '下载失败，请重试' : 'Download failed, please try again');
    } finally {
      setIsLoading(false);
    }
  };
  const docName = DOCUMENT_NAMES[documentType][locale] || DOCUMENT_NAMES[documentType].en;
  return <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-sm text-muted-foreground flex items-center gap-2">
        <File className="w-4 h-4" />
        {docName}
      </span>
      <div className="flex gap-2">
        <Button variant={variant} size="sm" onClick={() => handleDownload('md')} disabled={isLoading} className="gap-2">
          <Download className="w-4 h-4" />
          {locale === 'zh' ? 'Markdown' : 'Markdown'}
        </Button>
        <Button variant={variant} size="sm" onClick={() => handleDownload('txt')} disabled={isLoading} className="gap-2">
          <Download className="w-4 h-4" />
          {locale === 'zh' ? '纯文本' : 'Text'}
        </Button>
      </div>
    </div>;
}